import { useLayoutEffect, useRef, type ReactNode } from 'react';
import { motion, type HTMLMotionProps } from 'framer-motion';

import { observeElementResize } from '#/lib/observe-element-resize';

/**
 * The Card Grid's cards container. It does two jobs:
 *
 * 1. Scroll reveal — it's a `motion.div`, so the parent passes `variants` +
 *    `whileInView` here and the child cards stagger in off this single trigger.
 *
 * 2. Equal-width CTAs — it equalizes the width of single-button card CTAs across
 *    sibling cards so they all match the widest one. The buttons live in
 *    separate card containers, so this can't be done in pure CSS.
 *    It observes the grid container and only remeasures when that container's
 *    width changes (or when web fonts finish loading). Resetting button
 *    widths on every notification would restyle the grid and loop. When the
 *    buttons are full-width (stacked) they already match, so nothing is
 *    forced; the equal-width behavior only applies in the "hug" state where
 *    each button would otherwise size to its own label.
 *
 * The reveal animation doesn't disturb the measuring because button widths don't
 * change with opacity/translate.
 */
export function CardsContainer({
  className,
  children,
  ...motionProps
}: { className?: string; children: ReactNode } & HTMLMotionProps<'div'>) {
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const root = ref.current;
    if (!root) return;

    let lastContainerWidth = Number.NaN;
    const equalize = (force = false) => {
      const buttons = Array.from(
        root.querySelectorAll<HTMLElement>('[data-equal-cta]'),
      );
      if (buttons.length < 2) return;

      const containerWidth = root.getBoundingClientRect().width;
      // Resetting inline widths restyles the grid and retriggers this
      // observer. Skip when the container hasn't actually changed.
      if (!force && Math.abs(containerWidth - lastContainerWidth) < 1) return;
      lastContainerWidth = containerWidth;

      // Reset any forced width so each button reports its natural size.
      for (const button of buttons) button.style.width = '';

      const widths = buttons.map((b) => b.getBoundingClientRect().width);
      const max = Math.max(...widths);
      const min = Math.min(...widths);

      // Already equal (e.g. full-width stacked) — leave the CSS in charge.
      if (max - min < 1) return;

      const target = `${Math.ceil(max)}px`;
      for (const button of buttons) button.style.width = target;
    };

    equalize(true);

    const disconnect = observeElementResize(root, () => equalize());

    // Re-measure once web fonts settle, since they change text width.
    if (typeof document !== 'undefined' && 'fonts' in document) {
      document.fonts.ready.then(() => equalize(true)).catch(() => {});
    }

    return disconnect;
  }, []);

  return (
    <motion.div ref={ref} className={className} {...motionProps}>
      {children}
    </motion.div>
  );
}
